import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { MultiSelect } from 'primereact/multiselect';
import { BlockUI } from 'primereact/blockui';
import { companiesApi, driversApi } from '../../api/services';
import { setCompanies, NO_COMPANY } from '../../store';
import { useAlerts } from '../../custom/Alerts';
import { fmtDate } from '../../utils/dates';
import DriverForm from './DriverForm';
import PageHeader, { tableEmpty } from '../common/PageHeader';
import TableSearch, { useFitRows, useOdlozeno, PAGINATOR } from '../common/TableSearch';

export default function Drivers({ active }) {
    const alerts = useAlerts();
    const companies = useSelector((s) => s.companies.list);
    const [filters, setFilters] = useState({ firstName: '', lastName: '', companyIds: [] });
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0);
    const [loading, setLoading] = useState(false);
    const [editId, setEditId] = useState(undefined);
    const [sort, setSort] = useState({ field: null, order: null });
    /* pretraga po svemu (ime, prezime, JMBG, kompanija, kartica, datumi...) ide na server — tabela je straničena tamo */
    const [pretraga, setPretraga] = useState('');
    const trazi = useOdlozeno(pretraga.trim());
    const lastQuery = useRef(null);
    const dispatch = useDispatch();
    /* veličina strane prema visini ekrana (server vraća stranu te veličine) */
    const tableRef = useRef(null);
    const pageSize = useFitRows(tableRef, active, { dep: rows.length > 0 });

    const refresh = useCallback(async (pageIndex = page + 1, f = filters) => {
        setLoading(true);
        const q = { ...f, pretraga: trazi, pageIndex, pageSize };
        q.filtersModified = JSON.stringify(lastQuery.current) !== JSON.stringify({ ...f, pretraga: trazi });
        lastQuery.current = { ...f, pretraga: trazi };
        try {
            /* kao SyncService: uz vozače se osvežava i keš kompanija */
            companiesApi.all().then((l) => dispatch(setCompanies([NO_COMPANY, ...l]))).catch(() => {});
            const res = await driversApi.all(q);
            setRows(res.drivers);
            setTotal(res.totalCount);
            setPage(pageIndex - 1);
        } catch { /* toast */ }
        setLoading(false);
    }, [page, filters, dispatch, pageSize, trazi]);

    /* kao stari grid: sortiranje u okviru učitane strane (rastuće -> opadajuće -> bez) */
    const sortedRows = useMemo(() => {
        if (!sort.field || !sort.order) return rows;
        const val = (r) => (sort.field === 'company' ? (r.company ? r.company.name : '') : r[sort.field]);
        return [...rows].sort((a, b) => {
            const x = val(a), y = val(b);
            if (x == null) return 1 * sort.order;
            if (y == null) return -1 * sort.order;
            return (x instanceof Date ? x - y : String(x).localeCompare(String(y), 'sr')) * sort.order;
        });
    }, [rows, sort]);

    useEffect(() => { refresh(1); }, [pageSize, trazi]); // eslint-disable-line

    const remove = async (d) => {
        if (!(await alerts.confirm('Potvrdi', `Da li ste sigurni da želite da obrišete vozača '${d.firstName} ${d.lastName} - ${d.uniqueId}'?`, { severity: 'danger' }))) return;
        try { await driversApi.remove(d.id); await refresh(1); } catch { /* toast */ }
    };

    const reset = () => setFilters({ firstName: '', lastName: '', companyIds: [] });

    return (
        <BlockUI blocked={loading} template={<i className="pi pi-spin pi-spinner" style={{ fontSize: '3rem' }} />}>
            <PageHeader title="Pregled vozača" subtitle="Vozači, njihove kompanije i tahografske kartice.">
                <Button icon="pi pi-refresh" outlined severity="secondary" tooltip="Osveži" tooltipOptions={{ position: 'bottom' }} aria-label="Osveži" onClick={() => refresh()} />
                <Button label="Dodaj vozača" icon="pi pi-plus" onClick={() => setEditId(0)} />
            </PageHeader>
            <div className="panel">
                <form className="filters" onSubmit={(e) => { e.preventDefault(); refresh(1); }}>
                    <div className="field">
                        <label htmlFor="drv-fn">Ime</label>
                        <InputText id="drv-fn" value={filters.firstName} onChange={(e) => setFilters({ ...filters, firstName: e.target.value })} />
                    </div>
                    <div className="field">
                        <label htmlFor="drv-ln">Prezime</label>
                        <InputText id="drv-ln" value={filters.lastName} onChange={(e) => setFilters({ ...filters, lastName: e.target.value })} />
                    </div>
                    <div className="field" style={{ flexBasis: '16rem' }}>
                        <label htmlFor="drv-co">Kompanija</label>
                        <MultiSelect inputId="drv-co" value={filters.companyIds} options={companies} optionLabel="name" optionValue="id" filter placeholder="Izaberite..."
                            maxSelectedLabels={1} selectedItemsLabel="{0} izabrano" onChange={(e) => setFilters({ ...filters, companyIds: e.value })} />
                    </div>
                    <div className="filters-actions" style={{ marginLeft: 0 }}>
                        <Button type="submit" label="Pretraži" icon="pi pi-search" />
                        <Button type="button" label="Ukloni" text severity="secondary" onClick={reset} />
                    </div>
                </form>
            </div>
            <div className="panel" ref={tableRef}>
                <TableSearch value={pretraga} onChange={setPretraga} placeholder="Pretraga (ime, prezime, JMBG, kompanija, kartica, datum...)" label="Pretraga vozača"
                    ukupno={trazi ? null : total} pronadjeno={trazi ? total : null} jedinica="vozača" />
                <DataTable value={sortedRows} dataKey="id" size="small" stripedRows lazy removableSort {...PAGINATOR}
                    sortField={sort.field} sortOrder={sort.order} onSort={(e) => setSort({ field: e.sortField, order: e.sortOrder })} first={page * pageSize} rows={pageSize}
                    totalRecords={total} onPage={(e) => refresh(e.page + 1)} emptyMessage={tableEmpty(trazi ? 'Nijedan vozač ne odgovara pretrazi' : 'Nijedan vozač nije pronađen', 'pi pi-users')}
                    onRowDoubleClick={(e) => setEditId(e.data.id)} rowClassName={() => 'row-clickable'}>
                    <Column header="" headerClassName="actions" bodyClassName="actions" style={{ width: '5.5rem' }} body={(d) => (
                        <span>
                            <Button icon="pi pi-pencil" rounded text severity="secondary" onClick={() => setEditId(d.id)} aria-label="Izmeni vozača" tooltip="Izmeni" />
                            <Button icon="pi pi-trash" rounded text severity="danger" onClick={() => remove(d)} aria-label="Obriši vozača" tooltip="Obriši" />
                        </span>
                    )} />
                    <Column field="firstName" header="Ime" sortable style={{ minWidth: '8rem' }} />
                    <Column field="lastName" header="Prezime" sortable style={{ minWidth: '8rem' }} />
                    <Column field="uniqueId" header="JMBG" sortable />
                    <Column field="birthDate" header="Datum rođenja" sortable body={(d) => fmtDate(d.birthDate)} />
                    <Column field="birthPlace" header="Mesto rođenja" sortable />
                    <Column field="birthCountry" header="Država rođenja" sortable />
                    <Column field="workStart" header="Radi od" sortable body={(d) => fmtDate(d.workStart)} />
                    <Column field="workEnd" header="Radi do" sortable body={(d) => fmtDate(d.workEnd)} />
                    <Column field="turistika" header="Turistika" sortable body={(d) => (d.turistika
                        ? <span className="pill ok">Turistika</span> : <span className="pill neutral">Gradski</span>)} />
                    <Column field="company" header="Kompanija" sortable style={{ minWidth: '10rem' }} body={(d) => (d.company ? d.company.name : <span className="muted">Bez kompanije</span>)} />
                </DataTable>
                <p className="panel-note"><i className="pi pi-info-circle" aria-hidden="true" />Dvoklik na red otvara izmenu vozača.</p>
            </div>
            {editId !== undefined && <DriverForm id={editId} onClose={() => setEditId(undefined)} onSaved={() => refresh(1)} />}
        </BlockUI>
    );
}
